import { DataScienceEnums, Task, TaskOverrides } from 'dashboard-services/api/dataScience';
import { ActionService } from 'dashboard-services/services';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { globalActions, loginActions } from 'primary-components';

import { basicPipelineCreatorActions, builderPipelineCreatorActions } from 'actions/modules/datascience';
import { routing } from 'routing';

export default (() => {
  const SAVE_PIPELINE_CREATOR_DATA_SCIENCE_PREFIX = "SAVE_PIPELINE_CREATOR_DATA_SCIENCE_PREFIX"

  const ON_CHANGE = 'ON_CHANGE_SAVE_DIALOG_CREATOR_DATA_SCIENCE'
  const onChange = ActionService.makeActionCreator(ON_CHANGE, 'value', 'name')

  const prepareTasks = ({ tasks, links }) => dispatch => {
    const tasksToSave = [];
    Object.entries(tasks || {})
      .filter(([, task]) => task.name !== undefined && task.name !== "")
      .forEach(([id, task]) => {
        const newTask = new Task()
        newTask.taskId = id
        newTask.taskName = task.name
        newTask.taskVersion = task.version
        newTask.taskGroup = task.groupName
        newTask.taskReadableName = task.readableName
        newTask.taskCategory = task.category
        newTask.isPaused = task.isPaused
        const taskOverrides = new TaskOverrides();
        taskOverrides.params = {}
        const resolveValue = (value, valueType) => {
          if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.JSON) {
            return typeof value === 'object' ? JSON.stringify(value) : value;
          } else if(Array.isArray(value)) {
            return value.toString()
          } else if(typeof value === 'object' && value !== null && typeof value !== 'function') {
            return JSON.stringify(value)
          } else if(typeof value === 'string') {
            return value.trim()
          } else {
            return value
          }
        }
        (task.params || []).forEach(param => {
          if(!param.dynamic) {
            taskOverrides.params = Object.assign(taskOverrides.params, { [param.name]: resolveValue(param.value, param.valueType) })
          } else if(param.dynamic && param.type === "OUTPUT") {
            // set dynamic output
            const linkInner = links.find(link => link.output.task === id && link.output.name === param.name)
            taskOverrides.params = Object.assign(taskOverrides.params, { [param.name]: linkInner?.value ? linkInner.value : param.value })
          } else if(param.dynamic && param.type === "INPUT" && param.value !== "") {
            taskOverrides.params = Object.assign(taskOverrides.params, { [param.name]: resolveValue(param.value, param.valueType) })
          }
        })
        newTask.taskOverrides = taskOverrides;
        newTask.prevTasks = [...new Set(links.filter(link => link.input.task === id).map(link => ({ id: link.output.task })))]
        newTask.nextTasks = [...new Set(links.filter(link => link.output.task === id).map(link => ({ id: link.input.task })))]
        tasksToSave.push(newTask)
    })
    // taking care of input params
    Object.entries(tasks || {})
      .filter(([, task]) => task.name !== undefined && task.name !== "")
      .forEach(([id, task]) => {
        (task.params || []).filter(p => p.dynamic && p.type === "INPUT" && p.value === "").forEach(param => {
          // read the id
          const linkInner = links.find(link => link.input.task === id && link.input.name === param.name)
          if(!linkInner) {
            return;
          }
          const outputTask = tasksToSave.find(t => t.taskId === linkInner.output.task),
                value = outputTask.taskOverrides.params[linkInner.output.name]
          // write the id
          tasksToSave.find(t => t.taskId === id).taskOverrides.params[param.name] = value;
        });
        (task.params || []).filter(p => p.dynamic && p.type === "OUTPUT" && p.value === "").forEach(param => {
          const linkInner = links.find(link => link.output.task === id && link.output.name === param.name)
          if(!linkInner) {
            const generated = basicPipelineCreatorActions.generateDynamicId()
            tasksToSave.find(t => t.taskId === id).taskOverrides.params[param.name] = generated;
            dispatch(builderPipelineCreatorActions.changeTaskParam(id, generated, param.name))
          }
        })
      })
    return tasksToSave.map(t => t.serialize())
  }

  const savePipelineCore = ({ createPipelineCall, updatePipelineCall, navigate, groupName: defaultGroup, name: defaultName, tags: defaultTags, closeDialog, overWrittenTasks, shouldUpdate }) => (dispatch, getState) => {
    const creatorState = getState().datascienceState.creatorState,
          builderState = creatorState.builderState,
          tasks = builderState.tasks,
          links = builderState.links,
          basicState = creatorState.basicState,
          saveDialogState = creatorState.saveDialogState,
          serialized = dispatch(prepareTasks({ tasks: overWrittenTasks || tasks, links })),
          pipelineName = String(defaultName || saveDialogState.name).trim(),
          pipelineGroup = String(defaultGroup || saveDialogState?.groupName?.value || saveDialogState?.groupName).trim(),
          pipelineTags = (defaultTags || saveDialogState.tags || []).map(tagObj => tagObj?.value || tagObj),
          args = {
            name: pipelineName,
            groupName: pipelineGroup,
            markdownId: saveDialogState.markdownId,
            payload: JSON.stringify({ positions: builderState.positions, comments: builderState.comments, commentsVisible: basicState.commentsVisible }),
            tags: pipelineTags,
            tasks: serialized
          }

    let request
    if(shouldUpdate) {
      request = updatePipelineCall({ ...args, pipelineId: basicState.id })
    } else {
      request = createPipelineCall(args)
    }
    dispatch(basicPipelineCreatorActions.setSaved(true))
    return request
      .then(response => {
        dispatch(basicPipelineCreatorActions.setPipelineId(response.pipelineId))
        dispatch(builderPipelineCreatorActions.onChange(tasks, 'initTasks')) // we do not want to use overwritten here
        dispatch(builderPipelineCreatorActions.onChange(links, 'initLinks'))
        dispatch(onChange(pipelineGroup, "groupName"))
        dispatch(onChange(pipelineName, "name"))
        dispatch(onChange(pipelineTags, "tags"))
        return response;
      })
      .then(response => {
        const url = DashboardLinkFactory.dataScience.pipeline({ baseUrl: "", groupName: pipelineGroup, pipelineName })
        if(decodeURI(window.location.pathname) !== url) {
          navigate(url, { replace: true })
        }
        closeDialog?.()
        return response;
      })
  }

  const savePipeline = ({ createPipelineCall, updatePipelineCall, navigate, groupName, name, tags, closeDialog }) => (dispatch, getState) => 
    dispatch(savePipelineCore({ createPipelineCall, updatePipelineCall, navigate, groupName, name, tags, closeDialog, shouldUpdate: getState().datascienceState.creatorState.basicState.id }))
      .then(response => {
        dispatch(globalActions.getMessageStore()).success("Pipeline has been saved.")
        return response;
      })

  const copyPipeline = ({  createPipelineCall, updatePipelineCall, navigate, groupName, name, tags, closeDialog, overWrittenTasks }) => dispatch => 
    dispatch(savePipelineCore({ createPipelineCall, updatePipelineCall, navigate, groupName, name, tags, closeDialog, overWrittenTasks, shouldUpdate: false }))
      .then(response => {
        dispatch(globalActions.getMessageStore()).success("New Pipeline has been created.")
        return response;
      })

  const movePipeline = ({  createPipelineCall, updatePipelineCall, navigate, groupName, closeDialog }) => dispatch => 
    dispatch(savePipelineCore({ createPipelineCall, updatePipelineCall, navigate, groupName, closeDialog, shouldUpdate: true }))
      .then(response => {
        dispatch(globalActions.getMessageStore()).success("Pipeline has been moved to the new group.")
        return response;
      })

  const editPipeline = ({  createPipelineCall, updatePipelineCall, navigate, name, tags, closeDialog }) => dispatch => 
    dispatch(savePipelineCore({ createPipelineCall, updatePipelineCall, navigate, name, tags, closeDialog, shouldUpdate: true }))
      .then(response => {
        dispatch(globalActions.getMessageStore()).success("Pipeline has been saved.")
        return response;
      })

  const getIsBlockedByWarehouse = () => (_, getState) => {
    const hasWarehouseAccess = getState().datascienceState.creatorState.builderState.hasWarehouseAccess,
          shouldHaveWarehouseAccess = getState().datascienceState.creatorState.builderState.shouldHaveWarehouseAccess,
          isBlockedByWarehouse = hasWarehouseAccess === false && shouldHaveWarehouseAccess === true
    return isBlockedByWarehouse;
  }

  const openSaveDialog = () => (dispatch, getState) => {
    const executing = getState().datascienceState.creatorState.basicState.executing,
          hasWriteAccessTo = dispatch(loginActions.hasAccessTo(routing.dataScience.routing.creator.name, "W", routing.dataScience.name))
    if(executing || !hasWriteAccessTo || dispatch(getIsBlockedByWarehouse())) {
      return;
    }
    dispatch(onChange("Save pipeline", "title"))
    dispatch(onChange(true, "isOpen"))
  }

  const quickSave = ({ createPipelineCall, updatePipelineCall, navigate }) => (dispatch, getState) => {
    const executing = getState().datascienceState.creatorState.basicState.executing,
          tasks = getState().datascienceState.creatorState.builderState.tasks || {},
          hasWriteAccessTo = dispatch(loginActions.hasAccessTo(routing.dataScience.routing.creator.name, "W", routing.dataScience.name)),
          { name: pipelineName, groupName: pipelineGroupName } = getState().datascienceState.creatorState.saveDialogState
    if(executing || !hasWriteAccessTo || basicPipelineCreatorActions.hasAccessProblems(tasks) || dispatch(getIsBlockedByWarehouse())) {
      return;
    }
    if(pipelineName && pipelineGroupName) {
      dispatch(savePipeline({ createPipelineCall, updatePipelineCall, navigate }))
    } else {
      dispatch(openSaveDialog())
    }
  }

  const CLEAR_MODAL = 'CLEAR_MODAL_SAVE_DIALOG_CREATOR_DATA_SCIENCE'
  const clearModal = ActionService.makeActionCreator(CLEAR_MODAL)

  const getPipelineNameValidators = (errors = []) => [{
    message: errors?.find(e => e.field === "pipeline.name")?.message || "",
    validator: () => errors?.findIndex(e => e.field === "pipeline.name") === -1
  }]

  const getGroupNameValidators = (errors = []) => [{
    message: errors?.find(e => e.field === "pipeline.groupName")?.message || "",
    validator: () => errors?.findIndex(e => e.field === "pipeline.groupName") === -1
  }]

  const getTagsValidators = (errors = []) => [{
    message: errors?.find(e => String(e.field).includes("pipeline.tags"))?.message || "",
    validator: () => errors?.findIndex(e => String(e.field).includes("pipeline.tags")) === -1
  }]

  return {
    onChange,
    ON_CHANGE,
    savePipeline,
    copyPipeline,
    CLEAR_MODAL,
    clearModal,
    openSaveDialog,
    quickSave,
    movePipeline,
    editPipeline,
    getPipelineNameValidators,
    getGroupNameValidators,
    getTagsValidators,
    SAVE_PIPELINE_CREATOR_DATA_SCIENCE_PREFIX
  }
})()
